/**
 * @fileoverview Observatories Index Page
 * The Observatory Network - grid display of all observatories.
 * Features observatory.png background with atmospheric styling.
 * @module pages/observatories
 */

import { el } from "../shared/components/simpleRender.js";

/**
 * All observatory definitions with their metadata
 */
const OBSERVATORIES = [
  {
    id: "dollar_liquidity",
    title: "Dollar Liquidity Observatory",
    description: "Track USD liquidity flows, Fed balance sheet, and dollar stress indicators across the global system.",
    status: "LIVE",
    isPrimary: true,
    isLink: true,
    tags: ["dxy", "basis", "usd", "funding"]
  },
  {
    id: "fx_stress",
    title: "FX Stress Observatory",
    description: "Monitor currency pair breakdowns, carry trade unwinds, and emerging market FX capitulation signals.",
    status: "COMING SOON",
    isLink: false,
    tags: ["usdjpy", "eurusd", "em", "carry"]
  },
  {
    id: "credit_risk",
    title: "Credit & Risk Observatory",
    description: "High-yield spreads, credit default swaps, and systemic risk indicators in one gothic dashboard.",
    status: "COMING SOON",
    isLink: false,
    tags: ["hyg", "cds", "spreads", "risk"]
  },
  {
    id: "treasury_depth",
    title: "Treasury Depth",
    description: "Order book liquidity, auction demand, and real-time depth analysis across the curve.",
    status: "ROADMAP",
    isLink: false,
    tags: ["treasury", "auction", "depth", "curve"]
  },
  {
    id: "em_carry",
    title: "EM Carry Unwind Risk",
    description: "Track vulnerable EM positions, carry trade exposure, and contagion risk across frontier markets.",
    status: "ROADMAP",
    isLink: false,
    tags: ["em", "carry", "contagion", "frontier"]
  },
  {
    id: "funding_stress",
    title: "Funding Stress Index",
    description: "SOFR-FF spread, repo rates, and overnight funding market strain indicators.",
    status: "ROADMAP",
    isLink: false,
    tags: ["sofr", "repo", "overnight", "funding"]
  }
];

/**
 * Creates an SVG icon for observatory cards
 */
function createIcon(type) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.5");
  
  const icons = {
    dollar_liquidity: '<circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 10h8M8 14h8"/>',
    fx_stress: '<path d="M2 12h4l3-9 6 18 3-9h4"/>',
    credit_risk: '<path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>',
    treasury_depth: '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/>',
    em_carry: '<circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
    funding_stress: '<path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>'
  };
  
  svg.innerHTML = icons[type] || icons.dollar_liquidity;
  return svg;
}

/**
 * Renders a single observatory card
 */
function renderObservatoryCard(obs) {
  const statusClass = obs.status === "LIVE" ? "status-live" : 
                      obs.status === "COMING SOON" ? "status-soon" : "status-roadmap";
  
  const cardClasses = [
    "widget-card",
    "observatory-card",
    obs.isPrimary ? "observatory-primary" : "",
    obs.isLink ? "panel-link" : "observatory-coming"
  ].filter(Boolean).join(" ");

  const cardContent = [
    el("div", { className: "card-icon-wrap" }, []),
    el("h3", { className: "widget-title" }, [obs.title]),
    el("p", { className: "widget-caption" }, [obs.description]),
    el("div", { className: "card-footer" }, [
      el("div", { className: "card-tags" }, 
        obs.tags.map(tag => el("span", { className: "card-tag" }, [tag]))
      ),
      el("span", { className: `status-pill ${statusClass}` }, [obs.status])
    ])
  ];

  const card = obs.isLink
    ? el("a", { href: `#/observatories/${obs.id}`, className: cardClasses }, cardContent)
    : el("div", { className: cardClasses }, cardContent);

  // Add icon
  const iconWrap = card.querySelector(".card-icon-wrap");
  if (iconWrap) {
    iconWrap.appendChild(createIcon(obs.id));
  }

  return card;
}

/**
 * Renders the observatories index page with atmospheric background
 * @returns {HTMLElement} Observatories page element
 */
export function renderObservatoriesPage() {
  return el("div", { className: "observatory-network-page" }, [
    // Background layers
    el("div", { className: "observatory-bg-layer" }, []),
    el("div", { className: "observatory-overlay" }, []),
    
    // Content
    el("div", { className: "observatory-page-content" }, [
      // Hero Section
      el("section", { className: "observatory-hero" }, [
        el("h1", { className: "observatory-hero-title" }, ["The Observatory Network"]),
        el("p", { className: "observatory-hero-tagline" }, [
          "Real-time dashboards tracking the pulse of global liquidity, currency stress, and credit market dislocations."
        ]),
        el("p", { className: "observatory-hero-description" }, [
          "Each observatory provides actionable insights before the mainstream catches on. Select an observatory to begin monitoring."
        ])
      ]),
      
      // Observatory Grid
      el("section", { className: "observatory-grid-section" }, [
        el("div", { className: "observatory-grid" }, 
          OBSERVATORIES.map(obs => renderObservatoryCard(obs))
        )
      ])
    ])
  ]);
}

export { OBSERVATORIES };
